"use client";

/**
 * Vue partagée de la page contact (FR `/contact` · EN `/en/contact`).
 *
 * Compose Hero + sélecteur de type de demande + formulaire de conversion
 * (POST `/api/contact`) + sections SEO + CTA réservation. Contenu localisé via
 * CONTACT_UI[locale] et getUseCases(locale). Défaut `locale = "fr"` → sortie FR
 * identique (zéro régression). `lang="en"` n'est posé que pour EN.
 */
import Link from "next/link";
import { useState } from "react";
import type { Locale } from "@/config/i18n";
import { tr } from "@/config/localeText";
import { HeroSection } from "@/(global)/contact/components/HeroSection";
import { getUseCases } from "@/(global)/contact/components/constants";
import { UseCaseId } from "@/(global)/contact/components/types";
import { UseCaseSelector } from "@/(global)/contact/components/UseCaseSelector";
import { ContactForm } from "@/(global)/contact/components/ContactForm";
import { SeoSections } from "@/(global)/contact/components/SeoSections";
import { CONTACT_UI } from "@/(global)/contact/components/contactDict";

export default function ContactPageView({
  locale = "fr",
}: {
  locale?: Locale;
}): JSX.Element {
  const useCases = getUseCases(locale);
  const cta = tr(CONTACT_UI, locale).cta;
  const [selected, setSelected] = useState<UseCaseId>(useCases[0].id);

  return (
    <main
      className="bg-white py-12 text-black"
      lang={locale === "en" ? "en" : undefined}
    >
      <HeroSection locale={locale} />

      <section className="py-6">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-5 px-6 lg:grid-cols-12">
          <div className="lg:sticky lg:top-24 lg:col-span-4 lg:self-start">
            <UseCaseSelector
              selected={selected}
              useCases={useCases}
              onPickUseCase={setSelected}
              locale={locale}
            />
          </div>
          <ContactForm
            initialUseCases={useCases}
            selected={selected}
            locale={locale}
          />
        </div>
      </section>

      <SeoSections locale={locale} />
      <section className="pb-10 pt-2">
        <div className="mx-auto max-w-6xl px-6">
          <div className="rounded-xl border border-black/10 bg-black/[0.02] p-4 text-sm text-black/80">
            {cta.text}{" "}
            <Link
              href={cta.href}
              className="font-semibold text-black underline"
            >
              {cta.link}
            </Link>
            .
          </div>
        </div>
      </section>
    </main>
  );
}
